// LogbookCalendar - Monats-Kalender für Logbuch-Einträge

import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import type { PlaceNote } from '../../types';
import { Timestamp } from '@firebase/firestore';
import { getMoodByValue } from '../../constants/moods';

interface LogbookCalendarProps {
  notes: PlaceNote[];
  selectedDate: Date | null;
  onSelectDate: (date: Date | null) => void;
}

function getDateFromTimestamp(date: Timestamp | Date | undefined): Date {
  if (!date) return new Date(0);
  if (date instanceof Timestamp) return date.toDate();
  return new Date(date);
}

function isSameDay(d1: Date, d2: Date): boolean {
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
}

function isSameMonth(d1: Date, d2: Date): boolean {
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth()
  );
}

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const MONTHS = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];

export function LogbookCalendar({
  notes,
  selectedDate,
  onSelectDate,
}: LogbookCalendarProps) {
  const [viewDate, setViewDate] = useState(new Date());
  const today = new Date();

  // Berechne Kalender-Tage für den aktuellen Monat
  const calendarDays = useMemo(() => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();

    // Erster Tag des Monats
    const firstDay = new Date(year, month, 1);
    // Letzter Tag des Monats
    const lastDay = new Date(year, month + 1, 0);

    // Wochentag des ersten Tages (0 = Sonntag, angepasst auf Montag = 0)
    let startDayOfWeek = firstDay.getDay() - 1;
    if (startDayOfWeek < 0) startDayOfWeek = 6;

    const days: { date: Date; isCurrentMonth: boolean }[] = [];

    // Tage vom vorherigen Monat
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const date = new Date(year, month, -i);
      days.push({ date, isCurrentMonth: false });
    }

    // Tage des aktuellen Monats
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }

    // Tage des nächsten Monats um auf 42 Tage zu kommen (6 Wochen)
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }

    return days;
  }, [viewDate]);

  // Map: Datum -> Notizen für diesen Tag
  const notesByDate = useMemo(() => {
    const map = new Map<string, PlaceNote[]>();
    notes.forEach((note) => {
      const date = getDateFromTimestamp(note.visitDate || note.createdAt);
      const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key)!.push(note);
    });
    return map;
  }, [notes]);

  const getNotesForDate = (date: Date): PlaceNote[] => {
    const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
    return notesByDate.get(key) || [];
  };

  const goToPreviousMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setViewDate(new Date());
    onSelectDate(null);
  };

  const handleDateClick = (date: Date, notesCount: number) => {
    if (notesCount === 0) return;

    if (selectedDate && isSameDay(selectedDate, date)) {
      // Deselect wenn bereits ausgewählt
      onSelectDate(null);
    } else {
      onSelectDate(date);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
        <button
          onClick={goToPreviousMonth}
          className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
        >
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>

        <div className="flex items-center gap-3">
          <h3 className="font-semibold text-gray-800">
            {MONTHS[viewDate.getMonth()]} {viewDate.getFullYear()}
          </h3>
          {!isSameMonth(viewDate, today) && (
            <button
              onClick={goToToday}
              className="text-xs text-indigo-600 hover:text-indigo-800 transition-colors"
            >
              Heute
            </button>
          )}
        </div>

        <button
          onClick={goToNextMonth}
          className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
        >
          <ChevronRight className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      {/* Wochentage */}
      <div className="grid grid-cols-7 border-b border-gray-200">
        {WEEKDAYS.map((day) => (
          <div
            key={day}
            className="py-2 text-center text-xs font-medium text-gray-500"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Kalender-Grid */}
      <div className="grid grid-cols-7">
        {calendarDays.map(({ date, isCurrentMonth }, index) => {
          const dayNotes = getNotesForDate(date);
          const hasNotes = dayNotes.length > 0;
          const isToday = isSameDay(date, today);
          const isSelected = selectedDate && isSameDay(date, selectedDate);

          // Erstes Mood für Emoji
          const firstMood = dayNotes[0]?.moods?.[0] || dayNotes[0]?.mood;
          const moodEmoji = firstMood ? getMoodByValue(firstMood)?.emoji : null;

          return (
            <button
              key={index}
              onClick={() => handleDateClick(date, dayNotes.length)}
              disabled={!hasNotes}
              className={`
                relative p-2 min-h-[60px] border-r border-b border-gray-100
                flex flex-col items-center justify-start gap-1
                transition-colors
                ${!isCurrentMonth ? 'bg-gray-50' : ''}
                ${hasNotes ? 'cursor-pointer hover:bg-indigo-50' : 'cursor-default'}
                ${isSelected ? 'bg-indigo-100 hover:bg-indigo-100' : ''}
              `}
            >
              {/* Tag-Nummer */}
              <span
                className={`
                  text-sm w-6 h-6 flex items-center justify-center rounded-full
                  ${!isCurrentMonth ? 'text-gray-300' : 'text-gray-700'}
                  ${isToday ? 'bg-indigo-500 text-white font-bold' : ''}
                  ${isSelected && !isToday ? 'ring-2 ring-indigo-500' : ''}
                `}
              >
                {date.getDate()}
              </span>

              {/* Einträge-Indikator */}
              {hasNotes && (
                <div className="flex items-center gap-0.5">
                  {moodEmoji && (
                    <span className="text-xs">{moodEmoji}</span>
                  )}
                  {dayNotes.length > 1 && (
                    <span className="text-xs text-indigo-600 font-medium">
                      +{dayNotes.length - 1}
                    </span>
                  )}
                </div>
              )}

              {/* Foto-Indikator */}
              {dayNotes.some((n) => n.photoMetadata && n.photoMetadata.length > 0) && (
                <div className="absolute bottom-1 right-1 w-2 h-2 bg-amber-400 rounded-full" />
              )}
            </button>
          );
        })}
      </div>

      {/* Legende */}
      <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 flex items-center justify-center gap-4 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 bg-indigo-500 rounded-full inline-block" />
          Heute
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 bg-amber-400 rounded-full inline-block" />
          Mit Foto
        </span>
      </div>

      {/* Ausgewähltes Datum Info */}
      {selectedDate && (
        <div className="px-4 py-3 bg-indigo-50 border-t border-indigo-200">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-indigo-800">
              {selectedDate.toLocaleDateString('de-DE', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
              })}
            </span>
            <span className="text-sm text-indigo-600">
              {getNotesForDate(selectedDate).length} Einträge
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default LogbookCalendar;
